// What to remind it of before it answers. No embeddings and no extra call: the store is a few hundred notes for
// months, so scoring in the browser is enough. Names matter most (she says 婆婆, it should know who that is),
// then character-bigram overlap for Chinese, then importance and recency. Predictions and follow-ups still open
// get a nudge so "you said this would happen" and "you were going to raise this when she's calm" surface.
import type { Memory } from '@/types';

const DAY = 24 * 60 * 60 * 1000;
const STRIP = /[\s\p{P}\p{S}]+/gu;

/** Character bigrams of the CJK runs plus whole Latin words, lower-cased. Pure; exported for tests. */
export function tokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const run of text.toLowerCase().split(STRIP)) {
    if (!run) continue;
    if (/^[a-z0-9]+$/.test(run)) {
      out.add(run);
      continue;
    }
    if (run.length === 1) out.add(run);
    for (let i = 0; i + 1 < run.length; i++) out.add(run.slice(i, i + 2));
  }
  return out;
}

export interface Scored {
  memory: Memory;
  score: number;
  overlap: number;
}

/** Pure; exported for tests. `overlap` is the part that comes from the query itself; zero means unrelated. */
export function scoreMemory(m: Memory, query: string, queryTokens: Set<string>, now: number): Scored {
  const q = query.toLowerCase();
  let subjectHits = 0;
  for (const s of m.subjects) {
    const needle = s.trim().toLowerCase();
    if (needle.length >= 1 && q.includes(needle)) subjectHits += 1;
  }
  const own = tokens(`${m.text} ${m.subjects.join(' ')}`);
  let shared = 0;
  for (const t of own) if (queryTokens.has(t)) shared += 1;
  const overlap = Math.min(subjectHits, 2) * 8 + Math.min(shared, 6) * 3;
  const pending = (m.kind === 'prediction' || m.kind === 'followup') && m.status !== 'done' ? 2 : 0;
  const age = now - m.updatedAt;
  const recency = age < 14 * DAY ? 2 : age < 30 * DAY ? 1 : 0;
  return { memory: m, score: overlap + m.importance + pending + recency, overlap };
}

export interface Retrieved {
  /** Everything of kind about_me: her standing profile, always present. */
  profile: Memory[];
  /** Notes related to what she just said, best first. */
  relevant: Memory[];
  /** Open follow-ups, for it to pick a moment (not to recite). */
  followups: Memory[];
}

export function retrieve(memories: Memory[], query: string, now = Date.now(), limit = 12): Retrieved {
  const queryTokens = tokens(query);
  const profile = memories.filter((m) => m.kind === 'about_me').sort((a, b) => b.importance - a.importance || a.createdAt - b.createdAt);
  const followups = memories
    .filter((m) => m.kind === 'followup' && m.status !== 'done')
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 5);
  const excluded = new Set([...profile, ...followups].map((m) => m.id));
  const relevant = memories
    .filter((m) => !excluded.has(m.id))
    .map((m) => scoreMemory(m, query, queryTokens, now))
    .filter((s) => s.overlap > 0)
    .sort((a, b) => b.score - a.score || b.memory.updatedAt - a.memory.updatedAt)
    .slice(0, limit)
    .map((s) => s.memory);
  return { profile, relevant, followups };
}

/** Notes that share a subject with the exchange, so extraction updates them instead of duplicating. */
export function related(memories: Memory[], text: string, limit = 20): Memory[] {
  const q = text.toLowerCase();
  const queryTokens = tokens(text);
  return memories
    .map((m) => scoreMemory(m, text, queryTokens, Date.now()))
    .filter((s) => s.overlap >= 6 || m_hasSubject(s.memory, q))
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, limit)
    .map((s) => s.memory);
}

function m_hasSubject(m: Memory, q: string): boolean {
  return m.subjects.some((s) => s.trim() && q.includes(s.trim().toLowerCase()));
}
